/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Informes mínimos tableside (E4): % pedido por clientes, respuesta a avisos, QRs — últimos 7 días.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { colors } from '../../shared/theme/colors';
import { ticketColors } from '../../shared/theme/ticketColors';
import { spacing } from '../../shared/theme/spacing';
import { typography } from '../../shared/theme/typography';
import {
  getTablesideMetrics,
  getTablesideSettings,
  type TablesideMetrics,
} from '../infra/hospitalityRepo';

export type TablesideMetricsSectionProps = {
  branchId: string | null | undefined;
};

function formatAckMs(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const sec = ms / 1000;
  if (sec < 60) return `${sec < 10 ? sec.toFixed(1) : Math.round(sec)} s`;
  return `${Math.round(sec / 60)} min`;
}

export function TablesideMetricsSection({ branchId }: TablesideMetricsSectionProps) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TablesideMetrics | null>(null);
  // Sin la feature prendida el bloque es tres ceros fijos — se oculta.
  const [enabled, setEnabled] = useState<boolean | null>(null);

  const load = useCallback(async () => {
    if (!branchId) {
      setData(null);
      setEnabled(null);
      return;
    }
    setLoading(true);
    try {
      const [settings, metrics] = await Promise.all([
        getTablesideSettings(branchId),
        getTablesideMetrics(branchId),
      ]);
      setEnabled(settings?.tablesideEnabled === true);
      setData(metrics);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!branchId) return null;
  if (enabled !== true) return null;

  const guestPct =
    data?.guestLinesPct != null ? `${data.guestLinesPct}%` : '—';

  return (
    <View style={styles.card} testID="hospitality.mesas.tableside-metrics">
      <Text style={styles.title}>Informes (7 días)</Text>
      <Text style={styles.desc}>Cuando el cliente pide solo desde la mesa.</Text>
      {loading ? (
        <ActivityIndicator color={ticketColors.gold} />
      ) : (
        <View style={styles.stats}>
          <View style={styles.stat}>
            <Text style={styles.statValue}>{guestPct}</Text>
            <Text style={styles.statLabel}>Agregado por clientes</Text>
          </View>
          <View style={styles.stat}>
            <Text style={styles.statValue}>{formatAckMs(data?.avgSignalAckMs ?? null)}</Text>
            <Text style={styles.statLabel}>Respuesta a avisos</Text>
          </View>
          <View style={styles.stat}>
            <Text style={styles.statValue}>{data?.qrBindsCount ?? 0}</Text>
            <Text style={styles.statLabel}>QR escaneados</Text>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    marginBottom: spacing.sm,
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 8,
  },
  title: { ...typography.body, color: colors.textPrimary, fontWeight: '700' },
  desc: { ...typography.caption, color: colors.textSecondary, lineHeight: 18 },
  stats: { flexDirection: 'row', gap: 8, marginTop: 4 },
  stat: { flex: 1, alignItems: 'flex-start', gap: 2 },
  statValue: { ...typography.body, color: ticketColors.ink, fontWeight: '700', fontSize: 18 },
  statLabel: { ...typography.caption, color: colors.textSecondary },
});
